import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Radio, Tag } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import classnames from 'classnames';

/**
 * One selectable printer inside the "Receipts print to" group. Presentational only — it
 * holds no Redux or API knowledge, so the view owns every decision about what a printer
 * can currently do.
 *
 * Must be rendered inside an antd `Radio.Group`. The radio is visually hidden and the whole
 * card is the hit area: that keeps keyboard and screen-reader selection working (arrow keys
 * still reach `Radio.Group`'s onChange) while the visible affordance is the check circle.
 * Hiding it also means a pointer click can only ever be counted once — a visible radio would
 * fire onChange *and* bubble to the card.
 */
export default function PrinterRow({
  value,
  selected,
  disabled,
  title,
  subtitle,
  widthUnset,
  canTest,
  testing,
  testDisabled,
  onSelect,
  onTestPrint,
  actions,
  footer,
}) {
  const { t } = useTranslation(null, { keyPrefix: 'Views:PrinterSettings:PrinterSettingsView' });

  // Nested controls sit inside the card's hit area, so they must not also flip the selection.
  const stopPropagation = (event) => event.stopPropagation();

  // The radio is a descendant, so activating it — by keyboard, or on its residual sr-only hit
  // area — dispatches a click that bubbles here. `Radio.Group`'s onChange has already reported
  // that selection; running onSelect too would save the same destination twice.
  const handleClick = (event) => {
    if (event.target.closest('.ant-radio-wrapper')) {
      return;
    }

    onSelect?.();
  };

  return (
    <div
      role="group"
      aria-label={title}
      onClick={disabled ? undefined : handleClick}
      className={classnames('rounded-lg border p-md transition-colors focus-within:ring-2 focus-within:ring-primary', {
        'border-primary': selected,
        'border-gray-200': !selected,
        'cursor-pointer': !disabled,
      })}
    >
      <div className="flex items-start gap-sm">
        {/* Fixed-size box so the text column doesn't shift when selection swaps the glyph. */}
        <span className="mt-nano flex h-md w-md shrink-0 items-center justify-center">
          {selected ? (
            <CheckCircleFilled className="text-primary text-md" />
          ) : (
            <span
              aria-hidden
              className={classnames('h-full w-full rounded-full border-2', {
                'border-gray-200': disabled,
                'border-gray-400': !disabled,
              })}
            />
          )}
        </span>
        {/* Named so a screen reader announces which printer the option selects, not just "radio". */}
        <Radio value={value} disabled={disabled} aria-label={title} className="sr-only" />
        <div className="min-w-0 flex-1 [&_.ant-btn]:min-w-[190px]">
          <p className="font-semibold">{title}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-nano">{subtitle}</p>}
          {widthUnset && (
            <Tag color="warning" className="mt-xs">
              {t('printers.external.width_unset')}
            </Tag>
          )}
          {canTest && (
            <div className="mt-sm" onClick={stopPropagation}>
              <Button loading={testing} disabled={testDisabled} onClick={onTestPrint}>
                {t('buttons.test_print')}
              </Button>
            </div>
          )}
          {actions && (
            <div className="mt-sm flex flex-wrap items-center gap-sm" onClick={stopPropagation}>
              {actions}
            </div>
          )}
          {footer && <div onClick={stopPropagation}>{footer}</div>}
        </div>
      </div>
    </div>
  );
}
